import { useEffect, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useIsAuthenticated } from "@azure/msal-react";
import { RootState } from "@/store/store";
import { userService, UserStatusResponse } from "@/services/userService";
import { useTheme } from "@/components/common/ThemeProvider";

export interface UserStatusState {
  isLoading: boolean;
  isActive: boolean | null;
  error: string | null;
  data: UserStatusResponse["data"] | null;
  isFirstCheck: boolean; // True only on first login, false on reloads
  isNoAgenciesError?: boolean; // True when user has no agencies assigned (403 error)
}

/**
 * Custom hook to fetch user status and sync theme preference
 * Automatically called when user is authenticated
 * 
 * This hook:
 * - Fetches user status from /user/status API
 * - Returns loading, active status, and error states
 * - Extracts the theme preference from the response
 * - Applies the theme using the ThemeProvider context
 * - Blocks access if user is inactive
 * - Tracks first check vs reload (to hide loader text on reload)
 */
export const useUserStatus = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const isMsalAuthenticated = useIsAuthenticated();
  const { setTheme } = useTheme();
  const hasInitialized = useRef(false);
  const isFetchingRef = useRef(false); // Prevent duplicate calls
  
  const [statusState, setStatusState] = useState<UserStatusState>({
    isLoading: false,
    isActive: null,
    error: null,
    data: null,
    isFirstCheck: !sessionStorage.getItem("user_status_checked"), // Check at render time
  });

  const fetchUserStatus = useCallback(async () => { // NOSONAR
    // Prevent duplicate calls (especially in React Strict Mode)
    if (isFetchingRef.current) {
      console.log("Status check already in progress, skipping...");
      return;
    }
    
    isFetchingRef.current = true;
    
    // Check if this is first check BEFORE we start loading
    const isFirst = !sessionStorage.getItem("user_status_checked");
    
    setStatusState((prev) => ({ 
      ...prev, 
      isLoading: true, 
      error: null,
      isFirstCheck: isFirst // Preserve the first check status during loading
    }));
    
    try {
      const response = await userService.getUserStatus();
      
      // Mark that we've checked status in this session
      sessionStorage.setItem("user_status_checked", "true");
      
      // Check if account is inactive (either from data or status code 423)
      const isAccountActive = response.status === 423 ? false : response.data.isActive;
      
      // Update state with response data
      setStatusState({
        isLoading: false,
        isActive: isAccountActive,
        error: null,
        data: response.data,
        isFirstCheck: false, // After first check, it's no longer first
        isNoAgenciesError: response.isNoAgenciesError || false, // Track if this is a "no agencies" error
      });
      
      // Only apply theme if account is active
      if (isAccountActive && response?.data?.theme) {
        const userTheme = response.data.theme;
        setTheme(userTheme, false); // false = don't sync to backend
        console.log(`User theme preference applied: ${userTheme}`);
      }
      
      // Log inactive account warning
      if (!isAccountActive) {
        console.warn("User account is inactive, redirecting to inactive page");
      }
      
      return response;
    } catch (error: any) {
      // Detect network errors and provide user-friendly message
      let errorMessage = "Failed to fetch user status";
      
      if (error) {
        // Check for network errors (no response from server)
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
          errorMessage = "Network Error";
        } else if (error.response?.data) {
          // Check for specific error messages
          const errorData = error.response.data;
          const apiErrorMessage = errorData.message || errorData.errorMessage || errorData.error || "";
          
          // Check for "User not authorized" error
          if (apiErrorMessage.toLowerCase().includes("user not authorized to access this resource")) {
            errorMessage = "This user does not have access to ONEview";
          } else if (error instanceof Error) {
            errorMessage = error.message;
          } else {
            errorMessage = apiErrorMessage || error.message;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
      }
      
      console.error("Failed to fetch and apply user status:", error);
      
      setStatusState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      
      throw error;
    } finally {
      isFetchingRef.current = false;
    }
  }, [setTheme]);

  useEffect(() => {
    // Only fetch user status when both Redux and MSAL are authenticated
    // This ensures tokens are available in new tabs before making API calls
    if (isAuthenticated && isMsalAuthenticated && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchUserStatus();
    }

    // Reset when user logs out (either Redux or MSAL logout)
    if ((!isAuthenticated || !isMsalAuthenticated) && hasInitialized.current) {
      hasInitialized.current = false;
      isFetchingRef.current = false; // Reset fetching flag
      setStatusState({
        isLoading: false,
        isActive: null,
        error: null,
        data: null,
        isFirstCheck: true, // Reset to first check on logout
        isNoAgenciesError: false,
      });
    }
  }, [isAuthenticated, isMsalAuthenticated, fetchUserStatus]);

  return { 
    fetchUserStatus, 
    ...statusState 
  };
};

